import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import { MainLayout } from '../MainLayout';
import { PageHeading } from '../layout/PageHeading';
import { PageSection } from '../layout/PageSection';
import { SavedLocallyIndicator } from '../layout/SavedLocallyIndicator';
import { type PianoKeyTone } from '../piano/KeyboardPlate';
import { PianoKeyboard } from '../piano/PianoKeyboard';
import { useTranslation } from '@/hooks/use-translation';
import { usePianoProfileState, usePianoProfileActions } from '@/hooks/store/use-piano-profile-store';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup } from '@/components/ui/radio-group';
import { RadioItem } from '@/components/app-ui/RadioGroupItem';
import { NumericSelector } from '@/components/app-ui/numeric-selector/NumericSelector';
import { ValueSelector } from '@/components/app-ui/value-selector/ValueSelector';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { useValidStartNotes } from '@/hooks/keyboard/valid-start-notes';
import { getNoteName } from '@/lib/music/theory/spn';
import type { Note } from '@/lib/music/theory/spn';
import { Standard88Layout } from '@/lib/piano/keyboard';
import { KeyColor } from '@/lib/piano/keyboard/key-color.enum';
import { cn } from '@/lib/utils';
type LayoutMode = 'standard' | 'custom';
export const PianoPage = () => {
  const $ = _c(206);
  const {
    t
  } = useTranslation();
  const {
    brand,
    model,
    serialNumber,
    keyCount,
    startNote
  } = usePianoProfileState();
  const {
    updateSingleState
  } = usePianoProfileActions();
  const {
    keyboard
  } = useKeyboard();
  let t0;
  if ($[0] !== keyCount || $[1] !== startNote?.letter || $[2] !== startNote?.octave) {
    t0 = () => {
      if (keyCount === Standard88Layout.length && startNote?.letter === Standard88Layout.startNote.letter && startNote?.octave === Standard88Layout.startNote.octave) {
        return "standard";
      }
      return "custom";
    };
    $[0] = keyCount;
    $[1] = startNote?.letter;
    $[2] = startNote?.octave;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const getInitialLayoutMode = t0;
  let t1;
  if ($[4] !== getInitialLayoutMode) {
    t1 = () => getInitialLayoutMode();
    $[4] = getInitialLayoutMode;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const [layoutMode, setLayoutMode] = useState(t1);
  const isCustomLayout = layoutMode === "custom";
  const {
    validStartNotes
  } = useValidStartNotes();
  let t2;
  if ($[6] !== validStartNotes) {
    t2 = validStartNotes.map(_temp);
    $[6] = validStartNotes;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const startNoteValues = t2;
  let T0;
  let T1;
  let T2;
  let lastKeyName;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== brand || $[9] !== isCustomLayout || $[10] !== keyCount || $[11] !== keyboard || $[12] !== layoutMode || $[13] !== model || $[14] !== serialNumber || $[15] !== startNote || $[16] !== startNoteValues || $[17] !== t || $[18] !== updateSingleState || $[19] !== validStartNotes) {
    const keys = keyboard.getKeys();
    const whiteKeyCount = keys.filter(_temp2).length;
    const firstKeyName = keys[0]?.name ?? "";
    lastKeyName = keys[keys.length - 1]?.name ?? "";
    let t15;
    if ($[36] !== keyboard) {
      t15 = keyboard.getKeyByName("C4")?.number;
      $[36] = keyboard;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    const middleCKeyNumber = t15;
    let t16;
    if ($[38] !== keyboard) {
      t16 = keyboard.getKeys();
      $[38] = keyboard;
      $[39] = t16;
    } else {
      t16 = $[39];
    }
    const allKeys = t16;
    const t17 = allKeys[allKeys.length - 1];
    let t18;
    if ($[40] !== allKeys[0] || $[41] !== t17) {
      t18 = new Map([allKeys[0], t17].filter(_temp3).map(_temp4));
      $[40] = allKeys[0];
      $[41] = t17;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    const rangeEndTones = t18;
    let t19;
    if ($[43] !== updateSingleState) {
      t19 = event => {
        updateSingleState("brand", event.target.value || null);
      };
      $[43] = updateSingleState;
      $[44] = t19;
    } else {
      t19 = $[44];
    }
    const handleBrandChange = t19;
    let t20;
    if ($[45] !== updateSingleState) {
      t20 = event_0 => {
        updateSingleState("model", event_0.target.value || null);
      };
      $[45] = updateSingleState;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    const handleModelChange = t20;
    let t21;
    if ($[47] !== updateSingleState) {
      t21 = event_1 => {
        updateSingleState("serialNumber", event_1.target.value || null);
      };
      $[47] = updateSingleState;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    const handleSerialNumberChange = t21;
    let t22;
    if ($[49] !== startNote || $[50] !== updateSingleState) {
      t22 = layoutType => {
        setLayoutMode(layoutType as LayoutMode);
        if (layoutType === "standard") {
          updateSingleState("keyCount", Standard88Layout.length);
          updateSingleState("startNote", Standard88Layout.startNote);
        } else {
          if (layoutType === "custom" && !startNote) {
            updateSingleState("startNote", Standard88Layout.startNote);
          }
        }
      };
      $[49] = startNote;
      $[50] = updateSingleState;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    const handleLayoutChange = t22;
    let t23;
    if ($[52] !== updateSingleState) {
      t23 = value => {
        updateSingleState("keyCount", value);
      };
      $[52] = updateSingleState;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    const handleKeyCountChange = t23;
    let t24;
    if ($[54] !== updateSingleState || $[55] !== validStartNotes) {
      t24 = noteName => {
        const note_0 = validStartNotes.find(candidate => getNoteName(candidate) === noteName);
        if (note_0) {
          updateSingleState("startNote", note_0);
        }
      };
      $[54] = updateSingleState;
      $[55] = validStartNotes;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    const handleStartNoteChange = t24;
    T2 = MainLayout;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SavedLocallyIndicator />;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    let t25;
    if ($[58] !== t) {
      t25 = t("Piano");
      $[58] = t;
      $[59] = t25;
    } else {
      t25 = $[59];
    }
    let t26;
    if ($[60] !== t) {
      t26 = t("This info will appear in the app and on your printed reports, helping you stay organized if you manage multiple instruments.");
      $[60] = t;
      $[61] = t26;
    } else {
      t26 = $[61];
    }
    if ($[62] !== t25 || $[63] !== t26) {
      t13 = <PageHeading title={t25} lede={t26} ledeWidth="64ch" />;
      $[62] = t25;
      $[63] = t26;
      $[64] = t13;
    } else {
      t13 = $[64];
    }
    let t27;
    if ($[65] !== t) {
      t27 = t("Identify your piano");
      $[65] = t;
      $[66] = t27;
    } else {
      t27 = $[66];
    }
    let t28;
    if ($[67] !== t) {
      t28 = t("Brand");
      $[67] = t;
      $[68] = t28;
    } else {
      t28 = $[68];
    }
    let t29;
    if ($[69] !== t28) {
      t29 = <Label htmlFor="piano-brand">{t28}</Label>;
      $[69] = t28;
      $[70] = t29;
    } else {
      t29 = $[70];
    }
    let t30;
    if ($[71] !== t) {
      t30 = t("Enter your piano brand");
      $[71] = t;
      $[72] = t30;
    } else {
      t30 = $[72];
    }
    const t31 = brand ?? "";
    let t32;
    if ($[73] !== handleBrandChange || $[74] !== t30 || $[75] !== t31) {
      t32 = <Input id="piano-brand" placeholder={t30} value={t31} onChange={handleBrandChange} />;
      $[73] = handleBrandChange;
      $[74] = t30;
      $[75] = t31;
      $[76] = t32;
    } else {
      t32 = $[76];
    }
    let t33;
    if ($[77] !== t29 || $[78] !== t32) {
      t33 = <div className="flex flex-col gap-[7px]">{t29}{t32}</div>;
      $[77] = t29;
      $[78] = t32;
      $[79] = t33;
    } else {
      t33 = $[79];
    }
    let t34;
    if ($[80] !== t) {
      t34 = t("Model");
      $[80] = t;
      $[81] = t34;
    } else {
      t34 = $[81];
    }
    let t35;
    if ($[82] !== t34) {
      t35 = <Label htmlFor="piano-model">{t34}</Label>;
      $[82] = t34;
      $[83] = t35;
    } else {
      t35 = $[83];
    }
    let t36;
    if ($[84] !== t) {
      t36 = t("Enter your piano model");
      $[84] = t;
      $[85] = t36;
    } else {
      t36 = $[85];
    }
    const t37 = model ?? "";
    let t38;
    if ($[86] !== handleModelChange || $[87] !== t36 || $[88] !== t37) {
      t38 = <Input id="piano-model" placeholder={t36} value={t37} onChange={handleModelChange} />;
      $[86] = handleModelChange;
      $[87] = t36;
      $[88] = t37;
      $[89] = t38;
    } else {
      t38 = $[89];
    }
    let t39;
    if ($[90] !== t35 || $[91] !== t38) {
      t39 = <div className="flex flex-col gap-[7px]">{t35}{t38}</div>;
      $[90] = t35;
      $[91] = t38;
      $[92] = t39;
    } else {
      t39 = $[92];
    }
    let t40;
    if ($[93] !== t) {
      t40 = t("Serial Number");
      $[93] = t;
      $[94] = t40;
    } else {
      t40 = $[94];
    }
    let t41;
    if ($[95] !== t40) {
      t41 = <Label htmlFor="piano-serial">{t40}</Label>;
      $[95] = t40;
      $[96] = t41;
    } else {
      t41 = $[96];
    }
    let t42;
    if ($[97] !== t) {
      t42 = t("Enter your piano serial number");
      $[97] = t;
      $[98] = t42;
    } else {
      t42 = $[98];
    }
    const t43 = serialNumber ?? "";
    let t44;
    if ($[99] !== handleSerialNumberChange || $[100] !== t42 || $[101] !== t43) {
      t44 = <Input id="piano-serial" className="font-mono" placeholder={t42} value={t43} onChange={handleSerialNumberChange} />;
      $[99] = handleSerialNumberChange;
      $[100] = t42;
      $[101] = t43;
      $[102] = t44;
    } else {
      t44 = $[102];
    }
    let t45;
    if ($[103] !== t41 || $[104] !== t44) {
      t45 = <div className="flex flex-col gap-[7px]">{t41}{t44}</div>;
      $[103] = t41;
      $[104] = t44;
      $[105] = t45;
    } else {
      t45 = $[105];
    }
    let t46;
    if ($[106] !== t27 || $[107] !== t33 || $[108] !== t39 || $[109] !== t45) {
      t46 = <PageSection className="flex-[1_1_340px]" contentClassName="gap-[18px]" title={t27}>{t33}{t39}{t45}</PageSection>;
      $[106] = t27;
      $[107] = t33;
      $[108] = t39;
      $[109] = t45;
      $[110] = t46;
    } else {
      t46 = $[110];
    }
    let t47;
    if ($[111] !== t) {
      t47 = t("Keyboard layout");
      $[111] = t;
      $[112] = t47;
    } else {
      t47 = $[112];
    }
    let t48;
    if ($[113] !== t) {
      t48 = t("You can define your keyboard layout manually if it differs from the standard 88-key configuration.");
      $[113] = t;
      $[114] = t48;
    } else {
      t48 = $[114];
    }
    let t49;
    if ($[115] !== t48) {
      t49 = <p className="m-0 text-[13px] leading-[1.5] text-muted-foreground">{t48}</p>;
      $[115] = t48;
      $[116] = t49;
    } else {
      t49 = $[116];
    }
    let t50;
    if ($[117] !== t) {
      t50 = t("Keyboard layout");
      $[117] = t;
      $[118] = t50;
    } else {
      t50 = $[118];
    }
    let t51;
    if ($[119] !== t) {
      t51 = t("Standard 88 keys");
      $[119] = t;
      $[120] = t51;
    } else {
      t51 = $[120];
    }
    let t52;
    if ($[121] !== t) {
      t52 = t("From A0 to C8.");
      $[121] = t;
      $[122] = t52;
    } else {
      t52 = $[122];
    }
    let t53;
    if ($[123] !== t51 || $[124] !== t52) {
      t53 = <RadioItem value="standard" label={t51} description={t52} />;
      $[123] = t51;
      $[124] = t52;
      $[125] = t53;
    } else {
      t53 = $[125];
    }
    let t54;
    if ($[126] !== t) {
      t54 = t("Custom");
      $[126] = t;
      $[127] = t54;
    } else {
      t54 = $[127];
    }
    const t55 = !isCustomLayout;
    const t56 = !isCustomLayout && "opacity-50";
    let t57;
    if ($[128] !== t56) {
      t57 = cn("flex flex-wrap gap-[18px]", t56);
      $[128] = t56;
      $[129] = t57;
    } else {
      t57 = $[129];
    }
    let t58;
    if ($[130] !== t) {
      t58 = t("Number of keys");
      $[130] = t;
      $[131] = t58;
    } else {
      t58 = $[131];
    }
    let t59;
    if ($[132] !== t58) {
      t59 = <span className="ptl-label">{t58}</span>;
      $[132] = t58;
      $[133] = t59;
    } else {
      t59 = $[133];
    }
    let t60;
    if ($[134] !== handleKeyCountChange || $[135] !== keyCount) {
      t60 = <NumericSelector value={keyCount} onChange={handleKeyCountChange} minValue={85} maxValue={97} step={1} />;
      $[134] = handleKeyCountChange;
      $[135] = keyCount;
      $[136] = t60;
    } else {
      t60 = $[136];
    }
    let t61;
    if ($[137] !== t59 || $[138] !== t60) {
      t61 = <div className="flex flex-col gap-1.5">{t59}{t60}</div>;
      $[137] = t59;
      $[138] = t60;
      $[139] = t61;
    } else {
      t61 = $[139];
    }
    let t62;
    if ($[140] !== t) {
      t62 = t("First note");
      $[140] = t;
      $[141] = t62;
    } else {
      t62 = $[141];
    }
    let t63;
    if ($[142] !== t62) {
      t63 = <span className="ptl-label">{t62}</span>;
      $[142] = t62;
      $[143] = t63;
    } else {
      t63 = $[143];
    }
    let t64;
    if ($[144] !== startNote) {
      t64 = startNote ? getNoteName(startNote) : "A0";
      $[144] = startNote;
      $[145] = t64;
    } else {
      t64 = $[145];
    }
    let t65;
    if ($[146] !== handleStartNoteChange || $[147] !== startNoteValues || $[148] !== t64) {
      t65 = <ValueSelector currentValue={t64} onChange={handleStartNoteChange} values={startNoteValues} />;
      $[146] = handleStartNoteChange;
      $[147] = startNoteValues;
      $[148] = t64;
      $[149] = t65;
    } else {
      t65 = $[149];
    }
    let t66;
    if ($[150] !== t63 || $[151] !== t65) {
      t66 = <div className="flex flex-col gap-1.5">{t63}{t65}</div>;
      $[150] = t63;
      $[151] = t65;
      $[152] = t66;
    } else {
      t66 = $[152];
    }
    let t67;
    if ($[153] !== t55 || $[154] !== t57 || $[155] !== t61 || $[156] !== t66) {
      t67 = <div inert={t55} className={t57}>{t61}{t66}</div>;
      $[153] = t55;
      $[154] = t57;
      $[155] = t61;
      $[156] = t66;
      $[157] = t67;
    } else {
      t67 = $[157];
    }
    let t68;
    if ($[158] !== t54 || $[159] !== t67) {
      t68 = <RadioItem value="custom" label={t54}>{t67}</RadioItem>;
      $[158] = t54;
      $[159] = t67;
      $[160] = t68;
    } else {
      t68 = $[160];
    }
    let t69;
    if ($[161] !== handleLayoutChange || $[162] !== layoutMode || $[163] !== t50 || $[164] !== t53 || $[165] !== t68) {
      t69 = <RadioGroup value={layoutMode} onValueChange={handleLayoutChange} aria-label={t50}>{t53}{t68}</RadioGroup>;
      $[161] = handleLayoutChange;
      $[162] = layoutMode;
      $[163] = t50;
      $[164] = t53;
      $[165] = t68;
      $[166] = t69;
    } else {
      t69 = $[166];
    }
    let t70;
    if ($[167] !== t47 || $[168] !== t49 || $[169] !== t69) {
      t70 = <PageSection className="flex-[1_1_420px]" title={t47}>{t49}{t69}</PageSection>;
      $[167] = t47;
      $[168] = t49;
      $[169] = t69;
      $[170] = t70;
    } else {
      t70 = $[170];
    }
    if ($[171] !== t46 || $[172] !== t70) {
      t14 = <div className="flex flex-wrap items-stretch gap-6">{t46}{t70}</div>;
      $[171] = t46;
      $[172] = t70;
      $[173] = t14;
    } else {
      t14 = $[173];
    }
    T1 = PageSection;
    if ($[174] !== t) {
      t10 = t("Keyboard preview");
      $[174] = t;
      $[175] = t10;
    } else {
      t10 = $[175];
    }
    t11 = t("{{total}} keys \xB7 {{white}} white \xB7 {{black}} black \xB7 {{first}} \u2192 {{last}}", {
      black: keys.length - whiteKeyCount,
      first: firstKeyName,
      last: lastKeyName,
      total: keys.length,
      white: whiteKeyCount
    });
    T0 = PianoKeyboard;
    t7 = keys;
    t8 = rangeEndTones;
    t9 = t("Keyboard of {{count}} keys, from {{first}} to {{last}}", {
      count: keys.length,
      first: firstKeyName,
      last: lastKeyName
    });
    let t71;
    if ($[176] !== t) {
      t71 = t("key {{number}}", {
        number: 1
      });
      $[176] = t;
      $[177] = t71;
    } else {
      t71 = $[177];
    }
    const t72 = `${firstKeyName} · ${t71}`;
    if ($[178] !== t72) {
      t5 = {
        at: "start",
        text: t72
      };
      $[178] = t72;
      $[179] = t5;
    } else {
      t5 = $[179];
    }
    if ($[180] !== middleCKeyNumber) {
      t6 = middleCKeyNumber !== undefined ? [{
        at: middleCKeyNumber,
        isMuted: true,
        text: "C4"
      } as const] : [];
      $[180] = middleCKeyNumber;
      $[181] = t6;
    } else {
      t6 = $[181];
    }
    t4 = "end";
    t3 = t("key {{number}}", {
      number: keys.length
    });
    $[8] = brand;
    $[9] = isCustomLayout;
    $[10] = keyCount;
    $[11] = keyboard;
    $[12] = layoutMode;
    $[13] = model;
    $[14] = serialNumber;
    $[15] = startNote;
    $[16] = startNoteValues;
    $[17] = t;
    $[18] = updateSingleState;
    $[19] = validStartNotes;
    $[20] = T0;
    $[21] = T1;
    $[22] = T2;
    $[23] = lastKeyName;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t3;
    $[30] = t4;
    $[31] = t5;
    $[32] = t6;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    T0 = $[20];
    T1 = $[21];
    T2 = $[22];
    lastKeyName = $[23];
    t10 = $[24];
    t11 = $[25];
    t12 = $[26];
    t13 = $[27];
    t14 = $[28];
    t3 = $[29];
    t4 = $[30];
    t5 = $[31];
    t6 = $[32];
    t7 = $[33];
    t8 = $[34];
    t9 = $[35];
  }
  const t15 = `${t3} · ${lastKeyName}`;
  let t16;
  if ($[182] !== t15 || $[183] !== t4) {
    t16 = {
      at: t4,
      text: t15
    };
    $[182] = t15;
    $[183] = t4;
    $[184] = t16;
  } else {
    t16 = $[184];
  }
  let t17;
  if ($[185] !== t16 || $[186] !== t5 || $[187] !== t6) {
    t17 = [t5, ...t6, t16];
    $[185] = t16;
    $[186] = t5;
    $[187] = t6;
    $[188] = t17;
  } else {
    t17 = $[188];
  }
  let t18;
  if ($[189] !== T0 || $[190] !== t17 || $[191] !== t7 || $[192] !== t8 || $[193] !== t9) {
    t18 = <T0 keys={t7} toneByKeyNumber={t8} description={t9} labels={t17} />;
    $[189] = T0;
    $[190] = t17;
    $[191] = t7;
    $[192] = t8;
    $[193] = t9;
    $[194] = t18;
  } else {
    t18 = $[194];
  }
  let t19;
  if ($[195] !== T1 || $[196] !== t10 || $[197] !== t11 || $[198] !== t18) {
    t19 = <T1 title={t10} meta={t11}>{t18}</T1>;
    $[195] = T1;
    $[196] = t10;
    $[197] = t11;
    $[198] = t18;
    $[199] = t19;
  } else {
    t19 = $[199];
  }
  let t20;
  if ($[200] !== T2 || $[201] !== t12 || $[202] !== t13 || $[203] !== t14 || $[204] !== t19) {
    t20 = <T2 headerRight={t12}>{t13}{t14}{t19}</T2>;
    $[200] = T2;
    $[201] = t12;
    $[202] = t13;
    $[203] = t14;
    $[204] = t19;
    $[205] = t20;
  } else {
    t20 = $[205];
  }
  return t20;
};
function _temp(note) {
  return {
    label: getNoteName(note),
    value: getNoteName(note)
  };
}
function _temp2(key) {
  return key.color === KeyColor.White;
}
function _temp3(key_0) {
  return key_0 !== undefined;
}
function _temp4(key_1) {
  return [key_1.number, "marked" as const];
}